import { isWebCitationsEvent, type ChatMessage, type CodexNotification, type WebCitationsEvent } from '@reados/contracts';

type Value = Record<string, unknown>;
const identity = (threadId: unknown, turnId: unknown, itemId: unknown) => JSON.stringify([threadId, turnId, itemId]);

/** Provider annotations are presentation sideband. Both native identity and
 * the complete original source must agree before any range reaches the UI. */
export function projectWebCitations(messages: readonly ChatMessage[], events: readonly CodexNotification[]): Map<ChatMessage, Value[]> {
  const latest = new Map<string, WebCitationsEvent>();
  for (const event of events) {
    if (!isWebCitationsEvent(event)) continue;
    const p = event.params, key = identity(p.threadId, p.turnId, p.itemId), prior = latest.get(key);
    if (!prior || p.revision > prior.params.revision) latest.set(key, event);
  }
  const result = new Map<ChatMessage, Value[]>();
  for (const event of latest.values()) {
    const p = event.params;
    const matches = messages.filter(message => message.role === 'assistant'
      && message.id === p.itemId && message.threadId === p.threadId && message.turnId === p.turnId);
    if (matches.length !== 1 || matches[0].text !== p.text) continue;
    const ranges = new Map<string, Value>();
    for (const citation of p.citations) {
      const key = JSON.stringify([citation.startIndex, citation.endIndex]);
      let range = ranges.get(key);
      if (!range) {
        range = { startIndex: citation.startIndex, endIndex: citation.endIndex, status: 'success', referenceIDs: [], searchReferences: [] };
        ranges.set(key, range);
      }
      const ids = range.referenceIDs as string[], references = range.searchReferences as Value[];
      if (citation.sourceId && !ids.includes(citation.sourceId)) ids.push(citation.sourceId);
      if (!references.some(reference => reference.url === citation.url && reference.sourceID === citation.sourceId)) {
        references.push({ url: citation.url, title: citation.title, content: '', searchProviderKind: 'web',
          ...(citation.sourceId ? { sourceID: citation.sourceId } : {}) });
      }
    }
    result.set(matches[0], [...ranges.values()].sort((left, right) => Number(left.startIndex) - Number(right.startIndex)));
  }
  return result;
}
